import type { ProductTier } from '../../types/shared';

// Wallet and Balance Types
export interface Wallet {
  id: string;
  organization: string;
  balance: string; // Decimal as string from API
  currency: string;
  is_active: boolean;
}

export interface WalletTransaction {
  id: string;
  wallet: string;
  transaction_type: 'top_up' | 'charge' | 'refund' | 'adjustment';
  amount: string; // Decimal as string
  balance_after: string; // Decimal as string
  description: string;
  description_de?: string; // German translation from backend
  created: string; // ISO datetime
  metadata?: Record<string, unknown>;
  agent_id: string | null;
  agent_name: string | null;
}

// Payment Types
export interface Payment {
  id: string;
  organization: string;
  initiated_by: string;
  payment_method?: string;
  amount: string; // Decimal as string
  status: 'pending' | 'succeeded' | 'failed' | 'canceled' | 'refunded';
  stripe_payment_intent_id?: string;
  stripe_checkout_session_id?: string;
  stripe_customer_id?: string;
  created: string; // ISO datetime
  updated: string; // ISO datetime
}

export interface PaymentMethod {
  id: string;
  user: string;
  organization: string;
  stripe_payment_method_id: string;
  type: 'card';
  brand?: string;
  card_brand?: string; // Backend field name
  last4?: string;
  card_last4?: string; // Alternative field name from backend
  exp_month?: number;
  exp_year?: number;
  is_default: boolean;
  is_active: boolean;
  created: string; // ISO datetime
}

// Auto-Recharge Types
export interface AutoRechargeSettings {
  id: string;
  organization: string;
  created_by: string;
  status: 'enabled' | 'disabled';
  amount: string; // Decimal as string
  threshold: string; // Decimal as string
  payment_method?: string | { id: string; [key: string]: unknown };
  created: string; // ISO datetime
  updated: string; // ISO datetime
}

// Usage Types
export interface UsageRecord {
  id: string;
  organization: string;
  usage_type: 'call_minutes' | 'sms' | 'web_search' | 'phone_number' | 'base_fee';
  quantity: number;
  unit_price: string; // Decimal as string
  total_cost: string; // Decimal as string
  billing_period_start: string; // ISO date
  billing_period_end?: string; // ISO date
  metadata?: Record<string, unknown>;
  created: string; // ISO datetime
}

export interface UsageByType {
  cost: string;
  quantity: number;
  count: number;
}

export interface UsageSummary {
  total_usage: string;
  by_type: {
    call: UsageByType;
    sms: UsageByType;
    web_search: UsageByType;
    phone_number: UsageByType;
    base_fee?: UsageByType;
  };
  period: {
    start: string;
    end: string;
  };
}

// Top-Up Product Types
export type TopUpProductType = 'topup_50' | 'topup_100' | 'topup_150' | 'topup_300' | 'topup_500';

export interface TopUpProduct {
  type: TopUpProductType;
  amount: number;
  label: string;
}

// Checkout Types
export interface CheckoutSessionResponse {
  checkout_url: string;
  session_id: string;
}

// Direct Charge Response
export interface DirectChargeResponse {
  payment_intent_id: string;
  status: 'succeeded' | 'requires_action' | 'processing' | 'failed';
  base_amount: string; // Amount before tax (what gets credited to wallet)
  tax_amount: string; // Tax amount
  final_amount: string; // Total amount charged (base + tax)
  currency: string;
  payment_id: string;
  requires_action: boolean;
  client_secret: string | null;
  discount_applied?: {
    coupon_code: string;
    original_amount: string;
    discount_amount: string;
    discount_type: 'fixed' | 'percentage';
    discount_value: string;
  };
}

export interface CouponValidationResponse {
  valid: boolean;
  coupon_code?: string;
  original_amount?: string;
  discount_amount?: string;
  final_amount?: string;
  discount_type?: 'fixed' | 'percentage';
  discount_value?: string;
  error?: string;
}

// Pricing Profile Types
export interface PricingProfile {
  id: string | null;
  organization: {
    id: string;
    name: string;
  };
  name: string;
  tier?: ProductTier | null;
  // A shared tariff version (not individual prices).
  is_standard?: boolean;
  minute_price: string;
  minute_price_test: string;
  phone_number_monthly: string;
  web_search_price: string;
  sms_price: string;
  minimum_monthly_spend: string;
  monthly_base_fee: string;
  tax_percent: string | null;
  is_active: boolean;
  creator: {
    id: string;
    email: string;
    full_name: string;
  } | null;
  created: string | null;
  modified: string | null;
}

// Legacy Types (for backward compatibility)
export interface BillingMetrics {
  currentBalance: number;
  currentBalanceTrend: string;
  prePaidBalance: number;
  prePaidBalanceLastToppedUp: string;
  totalSpent: number;
  totalSpentTrend: string;
  paymentStatus: string;
  paymentStatusDescription: string;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  amount: number;
  status: 'active' | 'inactive';
  features: string[];
  lastToppedUp?: string;
  balance?: number;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  date: string;
  description: string;
  amount: number;
  status: 'paid' | 'pending' | 'overdue' | 'failed';
}

export interface UsageMetric {
  name: string;
  used: number;
  limit: number | 'unlimited';
  percentage: number;
  icon: string;
}

export interface BillingFilters {
  search: string;
  status: '' | 'paid' | 'pending' | 'overdue' | 'failed';
  dateRange: '' | '30' | '90' | '365';
  transactionType?: '' | 'top_up' | 'charge' | 'refund' | 'adjustment';
  agent?: string; // Agent UUID filter
}

export interface Plan {
  id: string;
  name: string;
  price: number;
  isCurrent: boolean;
  features: string[];
  description?: string;
}

// API Response Types
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// Helper function to get tax percent from pricing profile with fallback
export function getTaxPercent(pricingProfile: PricingProfile | null): number {
  if (pricingProfile?.tax_percent) {
    return parseFloat(pricingProfile.tax_percent);
  }
  // Fallback to default if null or profile doesn't exist
  return 19.0;
}
